import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
const { FiShield, FiUserPlus, FiClock, FiActivity, FiXCircle, FiCheckCircle, FiMoreVertical, FiCalendar, FiLock, FiX } = FiIcons;
import SafeIcon from '../../common/SafeIcon';

const AccessManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    display_name: '',
    access_start_at: new Date().toISOString().slice(0, 16),
    access_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    reason: '',
    permissions: []
  });

  const availablePermissions = [
    { id: 'dashboard.view', label: 'View Dashboard' },
    { id: 'bookings.view', label: 'View Bookings' },
    { id: 'bookings.manage', label: 'Manage Bookings' },
    { id: 'messages.view', label: 'View Messages' },
    { id: 'messages.respond', label: 'Respond to Messages' },
    { id: 'gallery.view', label: 'View Gallery' },
    { id: 'gallery.manage', label: 'Manage Gallery' },
    { id: 'content.view', label: 'View Content' },
    { id: 'content.manage', label: 'Manage Content' }
  ];

  useEffect(() => {
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const meRes = await fetch('/api/auth/me', { credentials: 'include' });
      const meData = await meRes.json();
      if (meData.status === 'success' && meData.user.role === 'super_admin') {
        setUser(meData.user);
        const listRes = await fetch('/api/auth/temporary-admins', { credentials: 'include' });
        const listData = await listRes.json();
        setAdmins(listData.data || []);
      }
    } catch (err) {
      console.error('Fetch data failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/auth/temporary-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteData),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.status === 'success') {
        setShowInviteModal(false);
        fetchData();
      } else {
        alert(data.message || data.error || 'Invitation failed');
      }
    } catch (err) {
      alert('Network error');
    }
  };

  const handleAction = async (id, action) => {
    const reason = prompt(`Reason for ${action}:`);
    if (!reason) return;

    try {
      const res = await fetch(`/api/auth/temporary-admins/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchData();
      } else {
        alert(data.message || data.error);
      }
    } catch (err) {
      alert('Action failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-100">
            <SafeIcon icon={FiShield} className="text-2xl" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Access Management</h1>
            <p className="text-gray-600 mt-1">Manage Temporary Administrator Accounts</p>
          </div>
        </div>
        <button 
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-200"
        >
          <SafeIcon icon={FiUserPlus} />
          Invite Temporary Admin
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Administrator</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Access Window</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Permissions</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-gray-900">{admin.display_name}</span>
                      <span className="text-xs text-gray-500">{admin.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      admin.status === 'active' ? 'bg-green-100 text-green-800' :
                      admin.status === 'pending_setup' ? 'bg-blue-100 text-blue-800' :
                      admin.status === 'revoked' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {admin.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiClock} className="text-gray-400" />
                      <span>Expires: {new Date(admin.access_expires_at).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(typeof admin.permissions === 'string' ? JSON.parse(admin.permissions || "[]") : (admin.permissions || [])).slice(0, 2).map(p => (
                        <span key={p} className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-md font-medium text-gray-600 border border-gray-200">
                          {p.split('.')[0]}
                        </span>
                      ))}
                      {((typeof admin.permissions === 'string' ? JSON.parse(admin.permissions || "[]") : (admin.permissions || [])).length > 2) && (
                        <span className="text-[10px] text-gray-400 font-medium">
                          +{(typeof admin.permissions === 'string' ? JSON.parse(admin.permissions || "[]") : (admin.permissions || [])).length - 2} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {admin.status === 'active' && (
                        <button 
                          onClick={() => handleAction(admin.id, 'disable')} 
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors border border-transparent hover:border-amber-100"
                          title="Disable Access"
                        >
                          <SafeIcon icon={FiXCircle} />
                        </button>
                      )}
                      {admin.status === 'temporarily_disabled' && (
                        <button 
                          onClick={() => handleAction(admin.id, 'enable')} 
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-green-100"
                          title="Enable Access"
                        >
                          <SafeIcon icon={FiCheckCircle} />
                        </button>
                      )}
                      {admin.status !== 'revoked' && (
                        <button 
                          onClick={() => handleAction(admin.id, 'revoke')} 
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                          title="Revoke Permanently"
                        >
                          <SafeIcon icon={FiLock} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <SafeIcon icon={FiUserPlus} className="text-4xl text-gray-200" />
                      <p className="font-medium">No temporary administrators found.</p>
                      <button 
                        onClick={() => setShowInviteModal(true)}
                        className="text-blue-600 hover:underline text-sm font-bold"
                      >
                        Invite your first team member
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="px-8 py-5 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Invite Temporary Administrator</h2>
              <button onClick={() => setShowInviteModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                  <input 
                    type="email" required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="admin@example.com"
                    value={inviteData.email}
                    onChange={e => setInviteData({...inviteData, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Display Name</label>
                  <input 
                    type="text" required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="Full Name"
                    value={inviteData.display_name}
                    onChange={e => setInviteData({...inviteData, display_name: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Access Start</label>
                  <input 
                    type="datetime-local" required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    value={inviteData.access_start_at}
                    onChange={e => setInviteData({...inviteData, access_start_at: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Access Expiry</label>
                  <input 
                    type="datetime-local" required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    value={inviteData.access_expires_at}
                    onChange={e => setInviteData({...inviteData, access_expires_at: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Reason for Access</label>
                <textarea 
                  required rows="2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Why is this temporary access being granted?"
                  value={inviteData.reason}
                  onChange={e => setInviteData({...inviteData, reason: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">Permissions</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availablePermissions.map(p => (
                    <label key={p.id} className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${
                      inviteData.permissions.includes(p.id) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-100 hover:bg-gray-50 text-gray-600'
                    }`}>
                      <input 
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={inviteData.permissions.includes(p.id)}
                        onChange={e => {
                          const perms = e.target.checked 
                            ? [...inviteData.permissions, p.id]
                            : inviteData.permissions.filter(item => item !== p.id);
                          setInviteData({...inviteData, permissions: perms});
                        }}
                      />
                      <span className="text-xs font-bold uppercase tracking-tight">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-6 flex justify-end gap-3 border-t">
                <button 
                  type="button" onClick={() => setShowInviteModal(false)}
                  className="px-6 py-3 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200 active:scale-95"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessManagement;